import React from 'react';
import { motion } from 'framer-motion';
import SectionShell from './Chapter';
import { SITE } from '@/constants/testIds';

// Uploaded portrait — do not replace with an AI-generated version.
const PORTRAIT_URL =
  'https://customer-assets-jai6qajn.emergentagent.net/job_cut-through/artifacts/oya4gkqk_ChatGPT%20Image%20Jul%2019%2C%202026%2C%2006_12_10%20PM.png';

const FOCUS = [
  'Strategic partnerships and joint ventures',
  'Promoter and stakeholder alignment',
  'Energy and industrial opportunities',
  'Government and institutional engagement',
  'Senior-level business development',
];

export const Team = () => {
  const ease = [0.22, 1, 0.36, 1];

  return (
    <SectionShell
      id="team"
      testId={SITE.team}
      theme="light"
      index="07"
      eyebrow="Team"
      title={
        <>
          Senior, hands-on and personally on every mandate.
        </>
      }
      intro="Clients work directly with senior advisors — not through them. Additional profiles will be added as the practice expands."
    >
      <article
        aria-labelledby="team-mahesh-name"
        data-testid="team-member-mahesh"
        className="grid grid-cols-12 gap-8 md:gap-14 items-start"
      >
        {/* Portrait — 4:5 */}
        <div className="col-span-12 md:col-span-5">
          <motion.figure
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-10% 0px' }}
            transition={{ duration: 0.9, ease }}
            className="relative overflow-hidden border border-[color:var(--rule-light-2)] rounded-sm bg-[color:var(--paper-2)]"
            style={{ aspectRatio: '4 / 5' }}
          >
            <picture>
              <source srcSet={PORTRAIT_URL} type="image/png" />
              <img
                src={PORTRAIT_URL}
                alt="Mahesh Kadaba, NoBS Advisors."
                loading="lazy"
                decoding="async"
                sizes="(min-width: 1024px) 480px, (min-width: 768px) 45vw, 92vw"
                draggable={false}
                className="absolute inset-0 w-full h-full object-cover object-[center_28%] filter contrast-[1.02] saturate-[0.94]"
              />
            </picture>
            {/* Match team-photo grade — subtle warm overlay */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none mix-blend-multiply opacity-15"
              style={{
                background:
                  'linear-gradient(180deg, rgba(122,92,30,0.0) 40%, rgba(122,92,30,0.28) 100%)',
              }}
            />
          </motion.figure>
          <figcaption className="mt-4 flex items-center justify-between small-caps text-[10.5px] text-[color:var(--charcoal-3)]">
            <span>Portrait — Mahesh Kadaba</span>
            <span>NoBS Advisors · Mumbai</span>
          </figcaption>
        </div>

        {/* Profile */}
        <div className="col-span-12 md:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-10% 0px' }}
            transition={{ duration: 0.9, ease }}
          >
            <p className="small-caps text-[11px] text-[color:var(--gold-3)]">
              Managing Partner
            </p>
            <h3
              id="team-mahesh-name"
              className="mt-3 font-serif text-[38px] md:text-[52px] leading-[1.02] tracking-[-0.02em] text-[color:var(--charcoal)]"
            >
              Mahesh Kadaba
            </h3>

            {/* Credential highlights */}
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[color:var(--charcoal)]">
              <span className="inline-flex items-center gap-2 small-caps text-[11px]">
                <span className="font-serif text-[20px] leading-none text-[color:var(--gold-3)]">
                  40
                </span>
                <span className="text-[color:var(--charcoal-2)]">
                  Year career
                </span>
              </span>
              <span className="h-3 w-px bg-[color:var(--rule-light-2)] hidden sm:block" />
              <span className="inline-flex items-center gap-2 small-caps text-[11px]">
                <span className="font-serif text-[20px] leading-none text-[color:var(--gold-3)]">
                  3×
                </span>
                <span className="text-[color:var(--charcoal-2)]">
                  Former CXO
                </span>
              </span>
              <span className="h-3 w-px bg-[color:var(--rule-light-2)] hidden sm:block" />
              <span className="inline-flex items-center gap-2 small-caps text-[11px]">
                <span className="font-serif text-[20px] leading-none text-[color:var(--gold-3)]">
                  ∞
                </span>
                <span className="text-[color:var(--charcoal-2)]">
                  Global expertise
                </span>
              </span>
            </div>

            <p className="mt-6 text-[15px] md:text-[16px] leading-[1.6] text-[color:var(--charcoal-2)] max-w-[560px]">
              Senior business leader supporting strategic partnerships,
              stakeholder alignment and complex transaction execution.
            </p>

            <div className="mt-8 h-px bg-[color:var(--rule-light)]" />

            {/* Draft note */}
            <p
              data-testid="team-draft-note"
              className="mt-5 inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-medium text-[color:var(--gold-3)]"
            >
              <span
                aria-hidden
                className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--gold-3)]"
              />
              Draft biography · Pending factual review
            </p>

            <p className="mt-6 font-serif text-[19px] md:text-[20px] leading-[1.55] tracking-[-0.005em] text-[color:var(--charcoal)] max-w-[620px]">
              {'Mahesh Kadaba is a senior business leader and strategic advisor with a '}
              <span className="text-[color:var(--gold-3)]">forty-year career</span>
              {' of global expertise across energy, mobility, industrial partnerships and business development. He has held '}
              <span className="text-[color:var(--gold-3)]">CXO roles three times</span>
              {' in his career, and works closely with promoters, institutional stakeholders and strategic counterparties to originate, structure and advance complex commercial relationships.'}
            </p>

            <p className="mt-5 text-[15px] leading-[1.7] text-[color:var(--charcoal-2)] max-w-[620px]">
              At NoBS Advisors, as Managing Partner, Mahesh leads senior
              stakeholder engagement, strategic partnerships, joint ventures
              and transaction execution. His approach combines relationship-led
              business development with practical commercial judgment and
              long-term promoter alignment.
            </p>

            {/* Areas of Focus */}
            <div className="mt-12">
              <p className="small-caps text-[11px] text-[color:var(--gold-3)]">
                Areas of Focus
              </p>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 max-w-[620px]">
                {FOCUS.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-3 py-2 border-b border-[color:var(--rule-light)] text-[14.5px] leading-[1.5] text-[color:var(--charcoal)]"
                  >
                    <span
                      aria-hidden
                      className="mt-[9px] block w-3 h-px bg-[color:var(--gold-3)] shrink-0"
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="mt-12 grid grid-cols-2 gap-6 max-w-[520px]">
              <div>
                <p className="small-caps text-[10.5px] text-[color:var(--charcoal-3)]">
                  Direct
                </p>
                <a
                  href="mailto:mk@no-bs.in"
                  className="mt-2 block font-serif text-[19px] md:text-[20px] text-[color:var(--charcoal)] hover:text-[color:var(--gold-3)] transition-colors"
                >
                  mk@no-bs.in
                </a>
              </div>
              <div>
                <p className="small-caps text-[10.5px] text-[color:var(--charcoal-3)]">
                  Line
                </p>
                <a
                  href="tel:+919324805849"
                  className="mt-2 block font-serif text-[19px] md:text-[20px] text-[color:var(--charcoal)] hover:text-[color:var(--gold-3)] transition-colors"
                >
                  +91 93248-05849
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </article>

      {/* Placeholder note for additional team members */}
      <p className="mt-16 md:mt-20 text-[12.5px] leading-[1.6] text-[color:var(--charcoal-3)] max-w-[620px] border-t border-[color:var(--rule-light)] pt-6">
        Additional senior advisors will be introduced as the practice expands.
        Engagements are always staffed at the senior level and led directly by
        the partner in charge of the mandate.
      </p>
    </SectionShell>
  );
};

export default Team;
